import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { ActionMapJson } from "@engine/input/ActionMap";
import { defaultActions } from "@engine/input/ActionMap";

const VERSION = 1;

// ----------------------------------------------------------------------------
// Settings — graphics, locale, UI scale
// ----------------------------------------------------------------------------

export interface SettingsState {
  uiScale: number;
  showFps: boolean;
  vsync: boolean;
  language: string;
  setUiScale: (v: number) => void;
  setShowFps: (v: boolean) => void;
  setVsync: (v: boolean) => void;
  setLanguage: (v: string) => void;
  reset: () => void;
}

const settingsDefault = {
  uiScale: 1,
  showFps: true,
  vsync: true,
  language: "en",
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...settingsDefault,
      setUiScale: (v) => set({ uiScale: v }),
      setShowFps: (v) => set({ showFps: v }),
      setVsync: (v) => set({ vsync: v }),
      setLanguage: (v) => set({ language: v }),
      reset: () => set(settingsDefault),
    }),
    { name: "mmorpg.settings", version: VERSION, storage: createJSONStorage(() => localStorage) },
  ),
);

// ----------------------------------------------------------------------------
// Audio buses — master / music / sfx / ambient / voice
// ----------------------------------------------------------------------------

export interface AudioState {
  master: number;
  music: number;
  sfx: number;
  ambient: number;
  voice: number;
  muted: boolean;
  set: (bus: keyof Omit<AudioState, "muted" | "set" | "toggleMute" | "reset">, v: number) => void;
  toggleMute: () => void;
  reset: () => void;
}

const audioDefault = {
  master: 1,
  music: 0.6,
  sfx: 1,
  ambient: 0.7,
  voice: 1,
  muted: false,
};

export const useAudioStore = create<AudioState>()(
  persist(
    (set) => ({
      ...audioDefault,
      set: (bus, v) => set({ [bus]: clamp01(v) } as Partial<AudioState>),
      toggleMute: () => set((s) => ({ muted: !s.muted })),
      reset: () => set(audioDefault),
    }),
    { name: "mmorpg.audio", version: VERSION, storage: createJSONStorage(() => localStorage) },
  ),
);

// ----------------------------------------------------------------------------
// Keybindings — JSON snapshot of ActionMap (consumed by RebindingPanel)
// ----------------------------------------------------------------------------

export interface KeybindingsState {
  json: ActionMapJson;
  setBinding: (action: string, codes: string[]) => void;
  reset: () => void;
}

const defaultJson: ActionMapJson = defaultActions().toJSON();

export const useKeybindingsStore = create<KeybindingsState>()(
  persist(
    (set) => ({
      json: defaultJson,
      setBinding: (action, codes) =>
        set((s) => ({
          json: {
            bindings: { ...s.json.bindings, [action]: codes },
          },
        })),
      reset: () => set({ json: defaultJson }),
    }),
    { name: "mmorpg.keybindings", version: VERSION, storage: createJSONStorage(() => localStorage) },
  ),
);

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
